import type { ReactElement, ReactNode } from 'react';

/**
 * 사이드바 화면 아이콘 (지시36 §4).
 *
 * 종전에는 `① Pipeline Builder` 처럼 라벨 앞의 **번호 표식**을 아이콘 대신 썼다.
 * 접으면 `①` 만 남는데, 그것만 보고 무엇인지 알 수 있는 사람은 이미 외운
 * 사람뿐이다.
 *
 * **외부 아이콘 세트를 들이지 않았다.** 직접 그린 인라인 SVG 다 —
 * `screens/PipelineBuilder/nodes/icons.tsx` 가 같은 이유로 이미 그렇게 하고 있고,
 * 그 판단은 여기서도 그대로 맞다:
 *   - 라이선스 게이트를 통과할 세트를 고를 필요가 없다. 우리가 그린 것이다.
 *   - **쓰는 것만 번들에 들어간다.** 세트를 넣으면 트리셰이킹에 기대야 하고,
 *     지시36 §4 가 지적한 340KB 번들에 아이콘 전체가 얹힐 위험이 생긴다.
 *   - 굵기·크기·색이 기존 아이콘과 저절로 맞는다(currentColor·strokeWidth 2).
 */
type IconProps = { size?: number };

function Svg({ size = 16, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** ① Pipeline Builder — 이어진 꼭짓점. */
function PipelinesIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="5" cy="6" r="2.5" />
      <circle cx="19" cy="12" r="2.5" />
      <circle cx="5" cy="18" r="2.5" />
      <path d="M7.5 6h4a2 2 0 0 1 2 2v2m0 4v0a2 2 0 0 1-2 2h-4" />
      <path d="M13.5 12h3" />
    </Svg>
  );
}

/** ② Runs — 실행(재생). */
function RunsIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <polygon points="10,8 17,12 10,16" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/**
 * ⑩ Pipeline Operator — **컨베이어 벨트** (지시160).
 *
 * ⚠ `RunsIcon` 을 돌려쓰지 않는다. 접으면 아이콘이 이름을 대신하는데, 같은 그림이
 *   둘이면 접힌 사이드바에서 **두 화면이 구별되지 않는다**(이 표가 있는 까닭 그대로).
 */
function OperatorIcon(props: IconProps) {
  return (
    <Svg {...props}>
      {/* 벨트 */}
      <line x1="3" y1="16" x2="21" y2="16" />
      {/* 롤러 둘 */}
      <circle cx="7" cy="18" r="2" />
      <circle cx="17" cy="18" r="2" />
      {/* 벨트 위를 지나는 상자 */}
      <rect x="9" y="8" width="6" height="6" />
    </Svg>
  );
}

/** ③ Agent I/O — 들어오고 나가는 것. */
function AgentIoIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3 8h11" />
      <polyline points="11,5 14,8 11,11" />
      <path d="M21 16H10" />
      <polyline points="13,13 10,16 13,19" />
    </Svg>
  );
}

/** ④ Approvals — 사람이 확인한 표시. */
function ApprovalsIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M20 6 9 17l-5-5" />
    </Svg>
  );
}

/** ⑤ Fleet — 여러 대의 기계. */
function FleetIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="4" width="18" height="6" rx="1.5" />
      <rect x="3" y="14" width="18" height="6" rx="1.5" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
      <line x1="7" y1="17" x2="7.01" y2="17" />
    </Svg>
  );
}

/** ⑥ Sessions — 대화 중인 것. */
function SessionsIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
    </Svg>
  );
}

/** ⑦ Settings — 조절기. */
function SettingsIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <line x1="4" y1="8" x2="20" y2="8" />
      <circle cx="9" cy="8" r="2.5" />
      <line x1="4" y1="16" x2="20" y2="16" />
      <circle cx="16" cy="16" r="2.5" />
    </Svg>
  );
}

/** ⑧ Resources — 쌓아 둔 것. */
function ResourcesIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <polygon points="12,3 21,7.5 12,12 3,7.5" />
      <polyline points="3,12 12,16.5 21,12" />
      <polyline points="3,16.5 12,21 21,16.5" />
    </Svg>
  );
}

/** ⑨ Access — 사람과 열쇠. 구성원·초대가 여기 있다(지시54). */
function AccessIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3 20c0-3.3 2.7-6 6-6" />
      <circle cx="17" cy="14" r="2.5" />
      <path d="M17 16.5V21M17 19h2.5" />
    </Svg>
  );
}

/** 경로 → 아이콘. 사이드바가 이 표만 보고 그린다. */
/** ⑪ Dashboard — 세 막대와 바닥선. 다른 아이콘(벨트·재생·기계)과 겹치지 않는 그림이다(지시188). */
function DashboardIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 20h16" />
      <path d="M7 16v-5" />
      <path d="M12 16V6" />
      <path d="M17 16v-8" />
    </Svg>
  );
}

/** ⑫ Database — 원통 셋(저장소). */
function DatabaseIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <ellipse cx="12" cy="6" rx="7" ry="3" />
      <path d="M5 6v12c0 1.7 3.1 3 7 3s7-1.3 7-3V6" />
      <path d="M5 12c0 1.7 3.1 3 7 3s7-1.3 7-3" />
    </Svg>
  );
}

export const NAV_ICON: Record<string, (props: IconProps) => ReactElement> = {
  '/pipelines': PipelinesIcon,
  '/runs': RunsIcon,
  '/agent-io': AgentIoIcon,
  '/approvals': ApprovalsIcon,
  '/fleet': FleetIcon,
  '/sessions': SessionsIcon,
  '/settings': SettingsIcon,
  '/resources': ResourcesIcon,
  '/access': AccessIcon,
  /* 지시160 — 벨트 위를 움직이는 것을 보는 자리라 «흐름» 을 그린다. */
  '/operator': OperatorIcon,
  /* 지시188 — 첫 화면. 오르내리는 막대 셋 — «수로 본다» 를 그린다. */
  '/dashboard': DashboardIcon,
  /* 지시220 — 플릿의 데이터베이스. */
  '/databases': DatabaseIcon,
};
